/**
 * Field-level error copy for the core sign-in, register and forgot-password screens.
 *
 * Those screens (core/auth, which the game can't edit) send whatever the
 * player typed straight to the API, so an empty or malformed field comes back
 * as the backend's raw class-validator text ("email must be an email"), and a
 * wrong password as its terse "Invalid credentials". The wording below is the
 * client's (Website QA 2026-10-21, round 2). The form is checked in the capture
 * phase on the screen root, which runs before the core submit handler on the
 * form and can stop it; whatever the server still rejects is reworded in the
 * error box, so raw validator text never reaches the player.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Backend message for a wrong email/password pair (core auth.service). */
const BACKEND_BAD_CREDENTIALS = /^invalid credentials\.?$/i;
export const INCORRECT_CREDENTIALS = 'Incorrect email or password.';
export const INVALID_EMAIL = 'Please enter a valid email address.';
export const FORM_REJECTED = 'Some details are not right. Please check the form and try again.';

/**
 * class-validator's default messages start with the DTO property name
 * ("email must be an email", "password must be longer than or equal to 8
 * characters"); Nest joins several with commas. Limits come from the auth DTOs
 * (backend/src/core/auth/dto).
 */
const SERVER_FIELD_COPY: ReadonlyArray<readonly [field: string, copy: string]> = [
  ['email', INVALID_EMAIL],
  ['password', 'Password must be 8 to 128 characters.'],
  ['username', 'Username must be 3 to 32 characters: letters, digits, _ and -.'],
];
const RAW_VALIDATOR = /(^|,)\s*[a-z][A-Za-z]* (must|should|may) /;
const startsWithField = (text: string, f: string) => new RegExp(`(^|,)\\s*${f} `).test(text);

/** Player-facing copy for a server error text, or null when it is already fine to show. */
export function serverErrorCopy(text: string): string | null {
  const t = text.trim();
  if (BACKEND_BAD_CREDENTIALS.test(t)) return INCORRECT_CREDENTIALS;
  const hit = SERVER_FIELD_COPY.find(([f]) => startsWithField(t, f));
  if (hit) return hit[1];
  return RAW_VALIDATOR.test(t) ? FORM_REJECTED : null;
}

export function loginFieldError(email: string, password: string): string | null {
  const e = email.trim();
  const hasPassword = password.length > 0;
  if (!e && !hasPassword) return 'Please enter your email address and password.';
  if (!e) return 'Please enter your email address.';
  if (!EMAIL_RE.test(e)) {
    return hasPassword ? INVALID_EMAIL : 'Please enter a valid email address and your password.';
  }
  if (!hasPassword) return 'Please enter your password.';
  return null;
}

/** Register: an empty email is just as invalid as a malformed one (QA round 2). */
export function registerFieldError(email: string): string | null {
  return EMAIL_RE.test(email.trim()) ? null : INVALID_EMAIL;
}

export function forgotFieldError(email: string): string | null {
  const e = email.trim();
  if (!e) return 'Please enter your email address.';
  if (!EMAIL_RE.test(e)) return INVALID_EMAIL;
  return null;
}

function guardForm(
  root: HTMLElement,
  formId: string,
  errorId: string,
  check: (fd: FormData) => string | null,
): () => void {
  const onSubmit = (ev: Event) => {
    const form = ev.target;
    if (!(form instanceof HTMLFormElement) || form.id !== formId) return;
    const message = check(new FormData(form));
    if (!message) return;
    ev.preventDefault();
    ev.stopPropagation();
    const err = root.querySelector<HTMLElement>(`#${errorId}`);
    if (err) {
      err.textContent = message;
      err.hidden = false;
    }
  };
  root.addEventListener('submit', onSubmit, true);
  return () => root.removeEventListener('submit', onSubmit, true);
}

/** Rewords whatever the core screen writes into its error box (server responses). */
function rewordServerErrors(root: HTMLElement, errorId: string): () => void {
  const err = root.querySelector<HTMLElement>(`#${errorId}`);
  if (!err) return () => {};
  const obs = new MutationObserver(() => {
    const copy = serverErrorCopy(err.textContent ?? '');
    if (copy && copy !== err.textContent) err.textContent = copy;
  });
  obs.observe(err, { childList: true, characterData: true, subtree: true });
  return () => obs.disconnect();
}

function guardAndReword(root: HTMLElement, formId: string, errorId: string, check: (fd: FormData) => string | null) {
  const unguard = guardForm(root, formId, errorId, check);
  const unreword = rewordServerErrors(root, errorId);
  return () => {
    unreword();
    unguard();
  };
}

const field = (fd: FormData, name: string) => String(fd.get(name) ?? '');

/** Sign-in: field checks before submit, server errors (wrong credentials, validator text) reworded. */
export function attachLoginMessages(root: HTMLElement): () => void {
  return guardAndReword(root, 'login-form', 'login-error', (fd) =>
    loginFieldError(field(fd, 'email'), field(fd, 'password')),
  );
}

export function attachRegisterMessages(root: HTMLElement): () => void {
  return guardAndReword(root, 'register-form', 'register-error', (fd) => registerFieldError(field(fd, 'email')));
}

export function attachForgotMessages(root: HTMLElement): () => void {
  return guardAndReword(root, 'forgot-form', 'forgot-error', (fd) => forgotFieldError(field(fd, 'email')));
}
